import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const C = {
  teal:'#00B4D8', navy:'#0D1B2A', success:'#10B981',
  border:'#E5E7EB', bg:'#F0F7FA', grey:'#6B7280', dark:'#1F2937',
  red:'#EF4444', orange:'#F59E0B', purple:'#8B5CF6', blue:'#3B82F6',
};

const PLATFORM_META = {
  zoom:        { label:'Zoom',        icon:'🎥', color:'#2D8CFF', bg:'#EFF6FF' },
  google_meet: { label:'Google Meet', icon:'📹', color:'#34A853', bg:'#F0FDF4' },
  teams:       { label:'Teams',       icon:'💼', color:'#5B5EA6', bg:'#F5F3FF' },
  skype:       { label:'Skype',       icon:'📞', color:'#00AFF0', bg:'#EFF6FF' },
};

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const today = new Date(); today.setHours(0,0,0,0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate()+1);
  if (d.getTime() === today.getTime()) return 'Today';
  if (d.getTime() === tomorrow.getTime()) return 'Tomorrow';
  return d.toLocaleDateString('en-GB', { weekday:'short', day:'numeric', month:'short' });
}

function CountdownBadge({ minutesUntil }) {
  const [mins, setMins] = useState(minutesUntil);
  useEffect(() => {
    const iv = setInterval(() => setMins(m => m - 1), 60000);
    return () => clearInterval(iv);
  }, []);
  if (mins <= 0)  return <span style={{ background:C.red, color:'#fff', fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20 }}>LIVE NOW</span>;
  if (mins <= 15) return <span style={{ background:'#FEF9C3', color:'#92400E', fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20 }}>Starting in {mins}m</span>;
  if (mins <= 60) return <span style={{ background:'#EFF6FF', color:'#1D4ED8', fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20 }}>{mins}m away</span>;
  const hrs = Math.floor(mins/60); const rem = mins % 60;
  return <span style={{ background:C.bg, color:C.grey, fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20 }}>{hrs}h {rem}m</span>;
}

export function TutorSessionCard() {
  const [sessions, setSessions] = useState([]);
  const [profile,  setProfile]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [copied,   setCopied]   = useState(false);
  const [notesModal, setNotesModal] = useState(null); // { booking_id, students, lesson_notes, homework_notes }
  const [studentNotes, setStudentNotes] = useState({}); // { booking_id: { attendance_mode, private_note } }
  const [submittingNotes, setSubmittingNotes] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [selectedMaterials, setSelectedMaterials] = useState([]); // array of material IDs
  const [matSearch, setMatSearch] = useState('');
  const [matSkill, setMatSkill] = useState('');
  const [expandedLesson, setExpandedLesson] = useState(null);  // booking_id with lesson open
  const [expandedMaterial, setExpandedMaterial] = useState(null); // material id with content open

  const openNotesModal = async (bookingId) => {
    try {
      const r = await api.get(`/api/tutor/bookings/${bookingId}/slot-students`);
      const init = {};
      (r.data.students || []).forEach(s => {
        init[s.booking_id] = { attendance_mode: s.attendance_mode || 'in_person', private_note: s.private_note || '' };
      });
      setStudentNotes(init);
      setNotesModal(r.data);
      // Pre-tick assigned materials
      setSelectedMaterials(r.data.assigned_material_ids || []);
      setMatSearch('');
      setMatSkill('');
      // Fetch materials for the student's level (use first student's level)
      const level = r.data.students?.[0]?.level || '';
      api.get('/api/tutor/lesson-materials', { params: { level } })
        .then(res => setMaterials(res.data.materials || []))
        .catch(() => {});
    } catch { alert('Failed to load session data'); }
  };

  const toggleMaterial = (id) => {
    setSelectedMaterials(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const submitNotes = async () => {
    if (!notesModal) return;
    setSubmittingNotes(true);
    try {
      const r = await api.post(`/api/tutor/bookings/${notesModal.booking_id}/complete-with-notes`, {
        lesson_notes: notesModal.lesson_notes || '',
        homework_notes: notesModal.homework_notes || '',
        student_notes: studentNotes,
      });
      // Save topic coverage if materials were selected
      if (selectedMaterials.length > 0) {
        await api.post('/api/tutor/session-topics', {
          booking_id: notesModal.booking_id,
          material_ids: selectedMaterials,
        }).catch(() => {});
      }
      alert(r.data.message || 'Session completed!');
      setNotesModal(null);
      api.get('/api/tutor/upcoming-sessions').then(res => setSessions(res?.data?.sessions || [])).catch(() => {});
    } catch (e) { alert(e.response?.data?.error || 'Failed to save notes'); }
    finally { setSubmittingNotes(false); }
  };

  useEffect(() => {
    Promise.all([
      api.get('/api/tutor/upcoming-sessions').catch(() => null),
      api.get('/api/tutor/profile').catch(() => null),
    ]).then(([sessRes, profRes]) => {
      setSessions(sessRes?.data?.sessions || []);
      setProfile(profRes?.data?.tutor || {});
    }).finally(() => setLoading(false));
  }, []);

  const copyLink = async (link) => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return null;

  const hasLink        = !!profile?.online_link;
  const onlineSessions = sessions.filter(s => s.is_online);
  const liveSessions   = onlineSessions.filter(s => s.is_live);

  return (
    <>
    <div style={{ marginBottom:24 }}>
      <div style={{ display:'flex', alignItems:'center', marginBottom:14 }}>
        <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:C.dark, display:'flex', alignItems:'center', gap:8 }}>
          Online Teaching
          {liveSessions.length > 0 && (
            <span style={{ background:C.red, color:'#fff', fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20 }}>
              {liveSessions.length} LIVE
            </span>
          )}
        </h3>
      </div>

      <div style={{
        background: hasLink ? '#F0FDF4' : '#FFF7ED',
        border: hasLink ? '1.5px solid #BBF7D0' : '1.5px solid #FED7AA',
        borderRadius:14, padding:'16px 20px', marginBottom:16,
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
          <div style={{ fontSize:28 }}>{hasLink ? '✅' : '⏳'}</div>
          <div style={{ flex:1, minWidth:200 }}>
            <div style={{ fontWeight:700, fontSize:14, color:C.dark, marginBottom:3 }}>
              {hasLink ? 'Your video room is set up' : 'Video room not assigned yet'}
            </div>
            {hasLink ? (
              <div style={{ fontSize:13, color:'#065F46', display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                <span>{PLATFORM_META[profile.online_platform]?.icon || '📹'} {PLATFORM_META[profile.online_platform]?.label || 'Video'}</span>
                <span style={{ color:'#6B7280' }}>·</span>
                <a href={profile.online_link} target="_blank" rel="noreferrer"
                  style={{ color:'#059669', textDecoration:'none', fontWeight:600 }}>
                  {profile.online_link.length > 45 ? profile.online_link.slice(0,45)+'...' : profile.online_link}
                </a>
              </div>
            ) : (
              <div style={{ fontSize:13, color:'#92400E' }}>
                Your school will assign a Google Meet room to you. You will see it here once it is ready.
              </div>
            )}
          </div>
          {hasLink && (
            <button onClick={() => copyLink(profile.online_link)}
              style={{ padding:'8px 14px', background:'#fff', border:'1px solid #BBF7D0', borderRadius:8, fontSize:12, color:'#065F46', fontWeight:600, cursor:'pointer' }}>
              {copied ? '✓ Copied!' : '📋 Copy link'}
            </button>
          )}
        </div>
      </div>

      {onlineSessions.length === 0 && (
        <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:14, padding:'16px 20px' }}>
          <div style={{ fontWeight:700, fontSize:13, color:C.dark, marginBottom:12 }}>How sessions work</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px,1fr))', gap:12 }}>
            {[
              { icon:'📅', title:'Admin books',              desc:'Admin schedules a session with you for a specific date and time' },
              { icon:'🔔', title:'You both get notified',    desc:'You and the student will see the session on your dashboards' },
              { icon:'🎥', title:'Session opens 15 min before', desc:'Your room link becomes active and the Join button appears for the student' },
              { icon:'✅', title:'Mark complete',             desc:'After the session, mark it complete in My Lessons to log your earnings' },
            ].map(step => (
              <div key={step.title} style={{ background:C.bg, borderRadius:10, padding:'12px 14px' }}>
                <div style={{ fontSize:22, marginBottom:6 }}>{step.icon}</div>
                <div style={{ fontWeight:700, fontSize:12, color:C.dark, marginBottom:4 }}>{step.title}</div>
                <div style={{ fontSize:12, color:C.grey, lineHeight:1.5 }}>{step.desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {onlineSessions.length > 0 && (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          <div style={{ fontSize:13, fontWeight:600, color:C.grey, marginBottom:2 }}>
            Upcoming online sessions ({onlineSessions.length})
          </div>
          {onlineSessions.map(session => {
            const spm  = PLATFORM_META[session.platform] || PLATFORM_META.google_meet;
            const live = session.is_live;
            const hasAssigned = (session.assigned_materials || []).length > 0;
            // minsUntilEnd < 0 means session has fully elapsed → allow completing
            const minsUntilEnd = session.minutes_until_end != null
              ? session.minutes_until_end
              : (session.minutes_until + (session.duration_min || 90));
            const canComplete = minsUntilEnd <= 0;
            return (
              <div key={session.booking_id} style={{
                background:'#fff', borderRadius:12,
                border: live ? '2px solid #EF4444' : '1.5px solid #E5E7EB',
                overflow:'hidden',
              }}>
                {/* Main row */}
                <div style={{ padding:'14px 18px', display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
                  <div style={{ width:38, height:38, borderRadius:'50%', background:'#8B5CF615', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>👤</div>
                  <div style={{ flex:1, minWidth:150 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:C.dark }}>{session.student_name}</div>
                    <div style={{ fontSize:12, color:C.grey, marginTop:2 }}>
                      {formatDate(session.date)} · {session.time_label} · {session.duration_min} min
                    </div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <span style={{ background:spm.bg, color:spm.color, fontSize:11, fontWeight:600, padding:'3px 9px', borderRadius:20 }}>{spm.icon} {spm.label}</span>
                    <CountdownBadge minutesUntil={session.minutes_until} />
                  </div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    {hasAssigned && (
                      <button
                        onClick={() => setExpandedLesson(prev => prev === session.booking_id ? null : session.booking_id)}
                        style={{ padding:'7px 12px', background: expandedLesson === session.booking_id ? '#F0FDF4' : '#F0F7FA', color: expandedLesson === session.booking_id ? '#065F46' : '#0D1B2A', border:'1px solid', borderColor: expandedLesson === session.booking_id ? '#BBF7D0' : '#E5E7EB', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                        {expandedLesson === session.booking_id ? '▲ Hide Lesson' : '📚 Today\'s Lesson'}
                      </button>
                    )}
                    {session.video_link ? (
                      <a href={session.video_link} target="_blank" rel="noreferrer"
                        style={{ padding:'8px 16px', background: live ? C.red : C.success, color:'#fff', borderRadius:8, fontWeight:700, fontSize:12, textDecoration:'none' }}>
                        {live ? '● Start Now' : '▶ Start Session'}
                      </a>
                    ) : (
                      <span style={{ padding:'8px 14px', background:'#FEF9C3', color:'#92400E', borderRadius:8, fontSize:12, fontWeight:600 }}>
                        ⏳ Room pending
                      </span>
                    )}
                    {session.video_link && (
                      <button onClick={() => copyLink(session.video_link)}
                        style={{ padding:'8px 10px', background:'#F3F4F6', color:C.grey, border:'none', borderRadius:8, fontSize:12, cursor:'pointer' }}>📋</button>
                    )}
                    {canComplete ? (
                      <button onClick={() => openNotesModal(session.booking_id)}
                        style={{ padding:'8px 14px', background:'#F5F3FF', color:'#7C3AED', border:'1px solid #DDD6FE', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                        📝 Complete
                      </button>
                    ) : (
                      <div style={{ textAlign:'center' }}>
                        <div style={{ padding:'7px 12px', background:'#F3F4F6', color:C.grey, borderRadius:8, fontSize:11, fontWeight:600, cursor:'not-allowed', border:'1px solid #E5E7EB' }}>
                          🔒 Ends in {minsUntilEnd}m
                        </div>
                        <div style={{ fontSize:10, color:C.grey, marginTop:2 }}>Available after session</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Today's Lesson panel */}
                {expandedLesson === session.booking_id && hasAssigned && (() => {
                  const SKILL_COLORS = {grammar:'#6366F1',vocabulary:'#059669',reading:'#F59E0B',writing:'#EF4444',speaking:'#00B4D8'};
                  const SKILL_BG = {grammar:'#EEF2FF',vocabulary:'#ECFDF5',reading:'#FFFBEB',writing:'#FFF1F2',speaking:'#F0F7FA'};
                  return (
                    <div style={{ borderTop:'1.5px solid #E5E7EB', background:'#F8FAFF', padding:'16px 20px' }}>
                      <div style={{ fontWeight:700, fontSize:13, color:'#0D1B2A', marginBottom:12 }}>📚 Today\'s Lesson Plan</div>
                      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                        {(session.assigned_materials || []).map(mat => {
                          const isOpen = expandedMaterial === mat.id;
                          const content = mat.content || {};
                          return (
                            <div key={mat.id} style={{ background:'#fff', borderRadius:10, border:'1px solid #E5E7EB', overflow:'hidden' }}>
                              <div
                                onClick={() => setExpandedMaterial(prev => prev === mat.id ? null : mat.id)}
                                style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 16px', cursor:'pointer' }}>
                                <div style={{ flex:1 }}>
                                  <div style={{ fontWeight:600, fontSize:13, color:'#0D1B2A', marginBottom:4 }}>{mat.title}</div>
                                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                                    <span style={{ fontSize:11, padding:'2px 8px', borderRadius:12, background:SKILL_BG[mat.skill_type]||'#F3F4F6', color:SKILL_COLORS[mat.skill_type]||'#6B7280', fontWeight:600 }}>{mat.skill_type}</span>
                                    <span style={{ fontSize:11, padding:'2px 8px', borderRadius:12, background:'#EFF6FF', color:'#1D4ED8', fontWeight:600 }}>{mat.cefr_level}</span>
                                    {mat.theme && <span style={{ fontSize:11, color:'#6B7280' }}>{mat.theme}</span>}
                                  </div>
                                </div>
                                <span style={{ color:'#6B7280', fontSize:16 }}>{isOpen ? '▲' : '▼'}</span>
                              </div>
                              {isOpen && (
                                <div style={{ padding:'0 16px 16px', borderTop:'1px solid #F3F4F6' }}>
                                  {mat.description && <div style={{ fontSize:13, color:'#374151', marginBottom:12, marginTop:12, lineHeight:1.6 }}>{mat.description}</div>}
                                  {/* Grammar explanation */}
                                  {content.explanation && (
                                    <div style={{ marginBottom:12 }}>
                                      <div style={{ fontWeight:600, fontSize:12, color:'#6366F1', marginBottom:6 }}>Grammar Rule</div>
                                      <div style={{ background:'#EEF2FF', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#312E81' }}>{content.explanation}</div>
                                    </div>
                                  )}
                                  {/* Examples */}
                                  {(content.examples || []).length > 0 && (
                                    <div style={{ marginBottom:12 }}>
                                      <div style={{ fontWeight:600, fontSize:12, color:'#059669', marginBottom:6 }}>Examples</div>
                                      <ul style={{ margin:0, paddingLeft:18 }}>
                                        {content.examples.map((ex, i) => <li key={i} style={{ fontSize:13, color:'#065F46', marginBottom:4 }}>{ex}</li>)}
                                      </ul>
                                    </div>
                                  )}
                                  {/* Vocabulary words */}
                                  {(content.words || []).length > 0 && (
                                    <div style={{ marginBottom:12 }}>
                                      <div style={{ fontWeight:600, fontSize:12, color:'#059669', marginBottom:6 }}>Vocabulary ({content.words.length} words)</div>
                                      <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                                        {content.words.slice(0,8).map((w, i) => (
                                          <span key={i} style={{ background:'#ECFDF5', color:'#065F46', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:500 }}>{typeof w === 'string' ? w : w.word}</span>
                                        ))}
                                        {content.words.length > 8 && <span style={{ fontSize:12, color:'#6B7280' }}>+{content.words.length-8} more</span>}
                                      </div>
                                    </div>
                                  )}
                                  {/* Exercises count */}
                                  {(content.exercises || content.questions || []).length > 0 && (
                                    <div style={{ background:'#F9FAFB', borderRadius:8, padding:'8px 12px', fontSize:12, color:'#6B7280' }}>
                                      📝 {(content.exercises || content.questions || []).length} exercises ready for students
                                    </div>
                                  )}
                                  {/* Reading passage preview */}
                                  {content.passage && (
                                    <div style={{ marginBottom:12 }}>
                                      <div style={{ fontWeight:600, fontSize:12, color:'#F59E0B', marginBottom:6 }}>Reading Passage</div>
                                      <div style={{ background:'#FFFBEB', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#92400E', lineHeight:1.6 }}>
                                        {content.passage.slice(0,200)}{content.passage.length>200?'...':''}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}
    </div>

    {/* Post-Session Notes Modal */}
    {notesModal && (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
        <div style={{ background:'#fff', borderRadius:16, padding:28, width:'100%', maxWidth:560, maxHeight:'90vh', overflowY:'auto' }}>
          <h3 style={{ margin:'0 0 4px', fontSize:17, fontWeight:700, color:'#0D1B2A' }}>📝 Session Notes</h3>
          <div style={{ fontSize:13, color:'#6B7280', marginBottom:20 }}>
            {new Date(notesModal.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long' })} · {notesModal.time_slot?.toString().padStart(2,'0')}:00 · {notesModal.duration_minutes} min · {notesModal.students?.length} student(s)
          </div>

          <label style={{ display:'block', fontSize:13, fontWeight:700, marginBottom:6, color:'#0D1B2A' }}>📚 Topics covered <span style={{color:'#EF4444'}}>*</span></label>
          <textarea rows={3} placeholder="e.g. Present Perfect tense, travel vocabulary, pronunciation practice..."
            value={notesModal.lesson_notes || ''}
            onChange={e => setNotesModal(m => ({...m, lesson_notes: e.target.value}))}
            style={{ width:'100%', border:'1px solid #E5E7EB', borderRadius:8, padding:'10px 12px', fontSize:13, resize:'vertical', marginBottom:16, boxSizing:'border-box' }} />

          {/* Lesson Material Picker */}
          <label style={{ display:'block', fontSize:13, fontWeight:700, marginBottom:6, color:'#0D1B2A' }}>📖 Lesson materials covered (optional)</label>
          <div style={{ border:'1px solid #E5E7EB', borderRadius:10, padding:'12px 14px', marginBottom:16, background:'#FAFAFA' }}>
            <div style={{ display:'flex', gap:8, marginBottom:8, flexWrap:'wrap' }}>
              <input
                placeholder="Search materials…"
                value={matSearch}
                onChange={e => setMatSearch(e.target.value)}
                style={{ flex:1, minWidth:120, border:'1px solid #E5E7EB', borderRadius:7, padding:'6px 10px', fontSize:12 }}
              />
              <select value={matSkill} onChange={e => setMatSkill(e.target.value)}
                style={{ border:'1px solid #E5E7EB', borderRadius:7, padding:'6px 8px', fontSize:12, background:'#fff' }}>
                <option value="">All skills</option>
                {['grammar','vocabulary','writing','reading','speaking'].map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>
                ))}
              </select>
            </div>
            {selectedMaterials.length > 0 && (
              <div style={{ marginBottom:8, display:'flex', flexWrap:'wrap', gap:4 }}>
                {selectedMaterials.map(id => {
                  const m = materials.find(x => x.id === id);
                  return m ? (
                    <span key={id} style={{ background:'#7C3AED15', color:'#7C3AED', border:'1px solid #DDD6FE', borderRadius:20, padding:'2px 10px', fontSize:11, fontWeight:600 }}>
                      ✓ {m.title}
                      <button onClick={() => toggleMaterial(id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#7C3AED', fontWeight:700, marginLeft:4, padding:0 }}>×</button>
                    </span>
                  ) : null;
                })}
              </div>
            )}
            <div style={{ maxHeight:140, overflowY:'auto', display:'flex', flexDirection:'column', gap:3 }}>
              {materials
                .filter(m =>
                  (!matSkill || m.skill_type === matSkill) &&
                  (!matSearch || m.title.toLowerCase().includes(matSearch.toLowerCase()) ||
                    (m.topic_tag||'').toLowerCase().includes(matSearch.toLowerCase()))
                )
                .slice(0, 30)
                .map(m => (
                  <label key={m.id} style={{
                    display:'flex', alignItems:'center', gap:8, padding:'5px 8px',
                    borderRadius:7, cursor:'pointer',
                    background: selectedMaterials.includes(m.id) ? '#F5F3FF' : 'transparent',
                  }}>
                    <input type="checkbox" checked={selectedMaterials.includes(m.id)} onChange={() => toggleMaterial(m.id)}
                      style={{ accentColor:'#7C3AED', width:13, height:13 }} />
                    <span style={{ fontSize:12, color:'#0D1B2A' }}>{m.title}</span>
                    <span style={{ fontSize:10, color:'#6B7280', marginLeft:'auto', whiteSpace:'nowrap' }}>
                      {m.cefr_level} · {m.skill_type}
                    </span>
                  </label>
                ))
              }
              {materials.length === 0 && (
                <div style={{ fontSize:12, color:'#9CA3AF', textAlign:'center', padding:8 }}>No materials loaded</div>
              )}
            </div>
          </div>

          <label style={{ display:'block', fontSize:13, fontWeight:700, marginBottom:6, color:'#0D1B2A' }}>✏️ Homework assigned</label>
          <textarea rows={2} placeholder="e.g. Write 5 sentences using Present Perfect. Due next session."
            value={notesModal.homework_notes || ''}
            onChange={e => setNotesModal(m => ({...m, homework_notes: e.target.value}))}
            style={{ width:'100%', border:'1px solid #E5E7EB', borderRadius:8, padding:'10px 12px', fontSize:13, resize:'vertical', marginBottom:20, boxSizing:'border-box' }} />

          <div style={{ fontSize:13, fontWeight:700, color:'#0D1B2A', marginBottom:10 }}>👥 Student Attendance</div>
          {notesModal.students?.map(s => (
            <div key={s.booking_id} style={{ background:'#F9FAFB', borderRadius:10, padding:'12px 16px', marginBottom:10 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                <div style={{ fontWeight:600, fontSize:13 }}>{s.name} <span style={{ color:'#6B7280', fontWeight:400 }}>· {s.level}</span></div>
                <div style={{ display:'flex', gap:6 }}>
                  {['in_person','remote','absent'].map(mode => (
                    <button key={mode}
                      onClick={() => setStudentNotes(n => ({...n, [s.booking_id]: {...(n[s.booking_id]||{}), attendance_mode: mode}}))}
                      style={{
                        padding:'4px 10px', fontSize:11, fontWeight:600, borderRadius:20, cursor:'pointer', border:'1.5px solid',
                        background: (studentNotes[s.booking_id]?.attendance_mode || 'in_person') === mode ? (mode==='absent'?'#FEE2E2':mode==='remote'?'#EFF6FF':'#F0FDF4') : '#fff',
                        color: (studentNotes[s.booking_id]?.attendance_mode || 'in_person') === mode ? (mode==='absent'?'#DC2626':mode==='remote'?'#1D4ED8':'#16A34A') : '#9CA3AF',
                        borderColor: (studentNotes[s.booking_id]?.attendance_mode || 'in_person') === mode ? (mode==='absent'?'#FCA5A5':mode==='remote'?'#BFDBFE':'#BBF7D0') : '#E5E7EB',
                      }}>
                      {mode === 'in_person' ? '🏫 In Person' : mode === 'remote' ? '📹 Remote' : '❌ Absent'}
                    </button>
                  ))}
                </div>
              </div>
              <input placeholder="Private note for this student (optional — only they see this)"
                value={studentNotes[s.booking_id]?.private_note || ''}
                onChange={e => setStudentNotes(n => ({...n, [s.booking_id]: {...(n[s.booking_id]||{}), private_note: e.target.value}}))}
                style={{ width:'100%', border:'1px solid #E5E7EB', borderRadius:7, padding:'7px 11px', fontSize:12, boxSizing:'border-box' }} />
            </div>
          ))}

          <div style={{ display:'flex', gap:10, marginTop:20, justifyContent:'flex-end' }}>
            <button onClick={() => setNotesModal(null)}
              style={{ padding:'10px 20px', border:'1px solid #E5E7EB', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>
              Cancel
            </button>
            <button onClick={submitNotes} disabled={submittingNotes || !notesModal.lesson_notes?.trim()}
              style={{ padding:'10px 24px', background:'#7C3AED', color:'#fff', border:'none', borderRadius:8, fontWeight:700, cursor:'pointer', fontSize:13, opacity: (!notesModal.lesson_notes?.trim() || submittingNotes) ? 0.6 : 1 }}>
              {submittingNotes ? 'Saving…' : '✓ Complete Session & Send Recap'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

export function StudentSessionCard() {
  const [sessions,      setSessions]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [isAiOnly,      setIsAiOnly]      = useState(false);   // AI Practice Only — no tutor sessions
  const [attendanceModes, setAttendanceModes] = useState({});  // bookingId -> 'in_person'|'remote'
  const [settingMode,   setSettingMode]   = useState(null);    // bookingId being updated

  useEffect(() => {
    api.get('/api/student/upcoming-sessions').catch(() => null)
      .then(res => {
        if (res?.data?.is_ai_only) { setIsAiOnly(true); return; }
        const s = res?.data?.sessions || [];
        setSessions(s);
        // Seed attendance modes from existing data
        const modes = {};
        s.forEach(sess => { modes[sess.booking_id] = sess.attendance_mode || 'in_person'; });
        setAttendanceModes(modes);
      })
      .finally(() => setLoading(false));
  }, []);

  async function handleSetMode(bookingId, mode) {
    setSettingMode(bookingId);
    try {
      await api.patch(`/api/student/bookings/${bookingId}/attendance-mode`, { mode });
      setAttendanceModes(prev => ({ ...prev, [bookingId]: mode }));
    } catch (err) {
      const msg = err?.response?.data?.error || 'Could not update attendance mode';
      alert(msg);
    } finally {
      setSettingMode(null);
    }
  }

  if (loading) return null;
  if (isAiOnly) return null; // AI Practice Only students have no human tutor sessions

  return (
    <div style={{ marginBottom:24, background:'#fff', borderRadius:14, padding:'20px 24px', border:'1.5px solid #E5E7EB', boxShadow:'0 2px 8px rgba(0,0,0,0.05)' }}>
      <h3 style={{ margin:'0 0 16px', fontSize:15, fontWeight:700, color:C.dark, display:'flex', alignItems:'center', gap:8 }}>
        Your Upcoming Sessions
        {sessions.some(s=>s.is_live) && (
          <span style={{ background:C.red, color:'#fff', fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20 }}>LIVE</span>
        )}
      </h3>

      {sessions.length === 0 ? (
        <div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))', gap:12, marginBottom:16 }}>
            {[
              { icon:'📅', title:'Session booked',    desc:'Your school schedules a video session with your tutor' },
              { icon:'🔔', title:'You get notified',  desc:'Session appears here with the date, time and tutor name' },
              { icon:'🎥', title:'Join 15 min before',desc:'A Join button appears when your session is about to start' },
              { icon:'✅', title:'Attend & progress', desc:'Complete the session — your tutor tracks your progress' },
            ].map(step => (
              <div key={step.title} style={{ background:C.bg, borderRadius:10, padding:'12px 14px' }}>
                <div style={{ fontSize:20, marginBottom:5 }}>{step.icon}</div>
                <div style={{ fontWeight:700, fontSize:12, color:C.dark, marginBottom:3 }}>{step.title}</div>
                <div style={{ fontSize:12, color:C.grey, lineHeight:1.5 }}>{step.desc}</div>
              </div>
            ))}
          </div>
          <div style={{ background:'#EFF6FF', borderRadius:10, padding:'12px 16px', fontSize:13, color:'#1E40AF' }}>
            No upcoming sessions in the next 7 days. Book a group session from the 'Book a Class' page, or wait for your school to schedule one.
          </div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {sessions.map(session => {
            const pm      = PLATFORM_META[session.platform] || PLATFORM_META.google_meet;
            const live    = session.is_live;
            const canJoin = session.can_join && session.video_link;
            const curMode = attendanceModes[session.booking_id] || 'in_person';
            const locked  = session.minutes_until <= 60;
            const isSetting = settingMode === session.booking_id;
            return (
              <div key={session.booking_id} style={{
                borderRadius:12, padding:'14px 18px',
                background: live ? '#FFF5F5' : C.bg,
                border: live ? '2px solid #EF4444' : '1px solid #E5E7EB',
              }}>
                {/* Top row: tutor info + platform + join button */}
                <div style={{ display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
                  <div style={{ width:40, height:40, borderRadius:'50%', background:'#00B4D820', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>👩‍🏫</div>
                  <div style={{ flex:1, minWidth:160 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:C.dark }}>Session with {session.tutor_name || 'Your Tutor'}</div>
                    <div style={{ fontSize:12, color:C.grey, marginTop:2 }}>{formatDate(session.date)} · {session.time_label} · {session.duration_min} min</div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <span style={{ background:pm.bg, color:pm.color, fontSize:11, fontWeight:600, padding:'3px 9px', borderRadius:20 }}>{pm.icon} {pm.label}</span>
                    <CountdownBadge minutesUntil={session.minutes_until} />
                  </div>
                  <div>
                    {session.delivery_mode === 'physical' ? (
                      <div style={{ padding:'8px 14px', background:'#F0FDF4', color:'#065F46', borderRadius:10, fontSize:12, fontWeight:600 }}>📍 In-Person</div>
                    ) : canJoin ? (
                      <a href={session.video_link} target="_blank" rel="noreferrer"
                        style={{ display:'inline-block', padding:'10px 20px', background: live ? C.red : C.teal, color:'#fff', borderRadius:10, fontWeight:700, fontSize:13, textDecoration:'none' }}>
                        {live ? '● Join Now' : 'Join Session'}
                      </a>
                    ) : session.video_link ? (
                      <div style={{ textAlign:'center' }}>
                        <div style={{ padding:'8px 14px', background:'#F3F4F6', color:C.grey, borderRadius:10, fontSize:12, fontWeight:600 }}>Opens in {session.opens_in_min}m</div>
                        <div style={{ fontSize:11, color:C.grey, marginTop:3 }}>Link opens 15 min before</div>
                      </div>
                    ) : (
                      <div style={{ padding:'8px 14px', background:'#FEF9C3', color:'#92400E', borderRadius:10, fontSize:12, fontWeight:600 }}>Awaiting link</div>
                    )}
                  </div>
                </div>

                {/* Attendance mode toggle */}
                <div style={{ marginTop:12, paddingTop:12, borderTop:'1px solid #E5E7EB', display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                  <span style={{ fontSize:12, color:C.grey, fontWeight:500 }}>How will you attend?</span>
                  {locked ? (
                    <span style={{ fontSize:12, color:C.grey, background:'#F3F4F6', padding:'4px 10px', borderRadius:8 }}>
                      {curMode === 'remote' ? '📹 Remote' : '🏫 In Person'} · locked 1h before
                    </span>
                  ) : (
                    <div style={{ display:'flex', gap:6 }}>
                      {[['in_person','🏫','In Person','#F0FDF4','#065F46'],['remote','📹','Remote','#EFF6FF','#1D4ED8']].map(([mode, icon, label, bg, color]) => (
                        <button key={mode}
                          onClick={() => handleSetMode(session.booking_id, mode)}
                          disabled={isSetting || curMode === mode}
                          style={{
                            padding:'5px 12px', borderRadius:8, fontSize:12, fontWeight:600,
                            cursor: curMode === mode ? 'default' : 'pointer',
                            background: curMode === mode ? bg : '#F3F4F6',
                            color: curMode === mode ? color : C.grey,
                            border: curMode === mode ? `1.5px solid ${color}40` : '1.5px solid #E5E7EB',
                            opacity: isSetting ? 0.6 : 1,
                            transition:'all 0.15s',
                          }}>
                          {icon} {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}